import { useCallback, useMemo, useState } from 'react';
import { DATA_ADAPTER } from '../config/adapters';
import { useAuth, useNotifs } from '../contexts/index';
import { isHouseAutoResultsEnabled, type HouseResultsSyncConfig } from '../core/rules/publishResultsCta';
import { usePresentAppError } from './usePresentAppError';

export type LotteryForceSource = 'florida' | 'newyork';
export type LotteryForceSession = 'midday' | 'evening';

type ForceResult = {
  ok?: boolean;
  reason?: string;
  skipped?: boolean;
  attempted?: boolean;
  minutesAfterDraw?: number;
  dayKey?: string;
};

function houseLotterySource(config: HouseResultsSyncConfig | null | undefined): LotteryForceSource | null {
  const source = String(config?.source ?? 'florida');
  if (source === 'florida' || source === 'newyork') return source;
  return null;
}

/** God / support / banker (house auto-sync): manual Florida or NY results fetch+publish. */
export function useForceLotterySync(
  t: Record<string, string>,
  opts: { houseConfig?: HouseResultsSyncConfig | null } = {},
) {
  const { user } = useAuth();
  const { push } = useNotifs();
  const presentAppError = usePresentAppError();
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const houseSource = useMemo(
    () => houseLotterySource(opts.houseConfig),
    [opts.houseConfig],
  );

  const canForce = useMemo(() => {
    if (DATA_ADAPTER !== 'firebase') return false;
    const role = user?.role;
    if (role === 'god' || role === 'support_agent') return true;
    if (role === 'banker') {
      return Boolean(houseSource && isHouseAutoResultsEnabled(opts.houseConfig));
    }
    return false;
  }, [opts.houseConfig, houseSource, user?.role]);

  const allowedSources = useMemo((): LotteryForceSource[] => {
    if (!canForce) return [];
    if (user?.role === 'banker') return houseSource ? [houseSource] : [];
    return ['florida', 'newyork'];
  }, [canForce, houseSource, user?.role]);

  const forceSync = useCallback(async (
    source: LotteryForceSource,
    session: LotteryForceSession,
  ) => {
    const key = `${source}:${session}`;
    if (!canForce || busyKey) return false;
    if (!allowedSources.includes(source)) return false;
    const confirmMsg = (t.lotteryForceSyncConfirm ?? 'Fetch and publish {source} {session} results now?')
      .replace('{source}', source)
      .replace('{session}', session);
    const confirmed = globalThis.confirm?.(confirmMsg) ?? true;
    if (!confirmed) return false;

    setBusyKey(key);
    try {
      const { forceLotteryResultsSync } = await import('../services/firebase/FunctionsService');
      const result = await forceLotteryResultsSync({ source, session }) as ForceResult;
      if (result?.ok) {
        push({
          cat: 'system',
          sev: 'success',
          title: t.lotteryForceSyncOk ?? 'Results sync completed',
          body: (t.lotteryForceSyncOkBody ?? '{source} {session} published (or already settled).')
            .replace('{source}', source)
            .replace('{session}', session),
        });
        return true;
      }
      push({
        cat: 'system',
        sev: 'warn',
        title: t.lotteryForceSyncFailed ?? 'Results sync did not publish',
        body: result?.reason
          ? String(result.reason)
          : (t.lotteryForceSyncFailedHint ?? 'Sources may still be incomplete. Try again in a few minutes.'),
      });
      return false;
    } catch (err) {
      const { copy } = presentAppError(err, { t });
      push({
        cat: 'system',
        sev: 'danger',
        title: copy?.title || t.lotteryForceSyncFailed || t.err_generic,
        body: copy?.hint || '',
      });
      return false;
    } finally {
      setBusyKey(null);
    }
  }, [allowedSources, busyKey, canForce, presentAppError, push, t]);

  return { canForce, busyKey, forceSync, allowedSources };
}
